import { unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "border-b border-catering-cream-400 bg-gradient-to-b from-catering-lemon-100 to-catering-cream-100 px-6 pb-4 pt-6" }
const _hoisted_2 = { class: "flex items-start gap-4" }
const _hoisted_3 = { class: "min-w-0 flex-1" }
const _hoisted_4 = { class: "m-0 mt-2.5 text-sm leading-relaxed text-catering-ink-600" }
const _hoisted_5 = { class: "mt-4 flex items-center gap-3" }
const _hoisted_6 = { class: "h-1.5 flex-1 overflow-hidden rounded-full border border-catering-cream-400 bg-catering-cream-300" }
const _hoisted_7 = { class: "font-catering-mono text-[11px] font-normal tracking-[0.08em] text-catering-ink-400" }
const _hoisted_8 = { class: "flex-1 overflow-y-auto px-6 py-5" }
const _hoisted_9 = {
  class: "sr-only",
  role: "status",
  "aria-live": "polite"
}
const _hoisted_10 = {
  key: 1,
  class: "flex flex-col gap-3"
}
const _hoisted_11 = { class: "flex items-center gap-3 border-t border-catering-cream-400 bg-catering-cream-200 px-6 py-3.5 text-xs text-catering-ink-600" }
const _hoisted_12 = { class: "flex-1" }

import { XMarkIcon } from '@heroicons/vue/24/solid'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import ImportWizardModal from '@/components/neue/ImportWizardModal.vue'
import {
  hasOpenedSetupDrawer,
  initialOpenStepKey,
  markSetupDrawerOpened
} from '@/components/setup/setupDrawerFirstOpen'
import { useSetupDrawer } from '@/components/setup/setupDrawerOpen'
import SetupReviewPanel from '@/components/setup/SetupReviewPanel.vue'
import SetupStep from '@/components/setup/SetupStep.vue'
import { setupDrawerCopy } from '@/components/setup/setupSurfaceCopy'
import SetupAssistantStep from '@/components/setup/steps/SetupAssistantStep.vue'
import SetupCustomersStep from '@/components/setup/steps/SetupCustomersStep.vue'
import SetupPaymentsStep from '@/components/setup/steps/SetupPaymentsStep.vue'
import SetupStoreDetailsStep from '@/components/setup/steps/SetupStoreDetailsStep.vue'
import {
  ASSISTANT_STEP,
  CUSTOMERS_STEP,
  PAYMENTS_STEP,
  stepStatus,
  STORE_DETAILS_STEP,
  useSetupState
} from '@/composables/setupState'
import { createFocusTrap } from '@/composables/useFocusTrap'
import { clearImportRun, useImportRunTracker } from '@/helpers/importRunTracker'
import api from '@/store/api'

const TITLE_ID = 'setup-drawer-title'


export default {
  __name: 'SetupDrawer',
  setup(__props) {

const TITLES = {
  [STORE_DETAILS_STEP]: 'Store details',
  [PAYMENTS_STEP]: 'Payments',
  [CUSTOMERS_STEP]: 'Customers',
  [ASSISTANT_STEP]: 'Your assistant'
}

const { visible, platform, steps, progress, complete, reviewed, refresh } = useSetupState((...args) =>
  api.call(...args)
)
const { isOpen, close, setAssistantStepActive } = useSetupDrawer()

const panelRef = ref(null)
const openKey = ref(null)
const importOpen = ref(false)
const showSteps = ref(false)
const { status: trackedImportStatus } = useImportRunTracker()
const announcement = ref('')
let trap = null

// The one predicate for the review narrative in here, and the header, the panel,
// its pill and the announcement all read it. The drawer is the surface that
// witnesses the local completion, so pending_review is still false at that
// instant and driving any of them off the stamp would have the header and the
// panel telling the merchant two different things on one screen. Outside the
// drawer nothing sees that event, so the stamp keeps governing there.
const underReview = computed(() => complete.value && !reviewed.value)

const drawerCopy = computed(() => setupDrawerCopy({ complete: complete.value, underReview: underReview.value }))

// complete is the only thing that decides this: it is what says there is nothing
// left to show, review or not. Asking again is the merchant's own choice.
const showPanel = computed(() => complete.value && !showSteps.value)

const titleFor = key => TITLES[key] || key

const stepViews = computed(() =>
  steps.value.map((step, position) => ({
    ...step,
    index: position + 1,
    title: titleFor(step.key),
    status: stepStatus(step)
  }))
)

const toggle = key => {
  openKey.value = openKey.value === key ? null : key
}

const advance = key => {
  const from = steps.value.findIndex(step => step.key === key)
  const after = steps.value.slice(from + 1).find(step => !step.complete)
  const before = steps.value.slice(0, Math.max(from, 0)).find(step => !step.complete)
  openKey.value = after?.key || before?.key || null
}

const onImported = () => refresh()

// The completion stamp is the server's, and it writes it on the read path. Steps
// finish here against a local merge, so without this read the merchant sits on
// the chase-your-steps narrative until something else fetches the state.
watch(complete, done => {
  if (done) refresh()
})

// An import the merchant closed the wizard on finishes somewhere behind them, so
// the step is ticked off by the same refresh the wizard would have triggered had
// they stayed watching it. Only a clean finish is cleared: a failed or lost run
// is left for the step to say something about.
watch(trackedImportStatus, async status => {
  if (status !== 'completed') return
  await refresh()
  clearImportRun()
})

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Steps finish outside the drawer too - Stripe verification clearing, payments
// connected from Settings - so App.vue's page-load fetch is not current by now.
const onOpened = async () => {
  refresh()
  // Nothing is expanded behind the review panel: a first-ever open would otherwise
  // hand the merchant step 1 already open the moment they ask for the steps back.
  openKey.value = showPanel.value ? null : initialOpenStepKey(steps.value, hasOpenedSetupDrawer())
  // Only a visit that had steps to show can have spent the first one.
  if (steps.value.length && !showPanel.value) markSetupDrawerOpened()
  await nextTick()
  if (!panelRef.value) return
  trap = createFocusTrap(panelRef.value, {
    onEscape: () => {
      if (!importOpen.value) close()
    }
  })
  trap.activate()
}

const onClosed = () => {
  importOpen.value = false
  showSteps.value = false
  trap?.deactivate()
  trap = null
}

watch(isOpen, open => (open ? onOpened() : onClosed()))

onMounted(() => {
  if (isOpen.value) onOpened()
})

watch(openKey, async key => {
  if (!key) return
  await nextTick()
  const card = document.getElementById(`setup-step-${key}`)
  if (card) card.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest' })
})

watch(
  () => steps.value.filter(step => step.complete).map(step => step.key),
  (now, before) => {
    const added = now.filter(key => !(before || []).includes(key))
    if (!added.length) return
    announcement.value =
      drawerCopy.value.announcement || `${titleFor(added[0])} is done. ${progress.value.label} complete.`
    if (added.includes(openKey.value)) advance(openKey.value)
  }
)

// Open is the whole condition, done or not: the step is pointing at a button on
// the far side of the screen, and a merchant re-reading it needs to be shown
// which button just as much as one seeing it for the first time.
const assistantAttention = computed(() => isOpen.value && openKey.value === ASSISTANT_STEP)

watch(assistantAttention, setAssistantStepActive, { immediate: true })

onBeforeUnmount(() => {
  trap?.deactivate()
  setAssistantStepActive(false)
})

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_unref(visible))
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createVNode(_Transition, {
            "enter-active-class": "transition-opacity duration-300 ease-out motion-reduce:transition-none",
            "enter-from-class": "opacity-0",
            "leave-active-class": "transition-opacity duration-200 ease-in motion-reduce:transition-none",
            "leave-to-class": "opacity-0"
          }, {
            default: _withCtx(() => [
              (_unref(isOpen))
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["fixed inset-0 bg-catering-ink-900/25 backdrop-blur-[1.5px]", importOpen.value ? 'z-20' : 'z-40']),
                    onClick: _cache[0] || (_cache[0] = (...args) => (_unref(close) && _unref(close)(...args)))
                  }, null, 2))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createVNode(_Transition, {
            "enter-active-class": "transition-transform duration-300 ease-out motion-reduce:transition-none",
            "enter-from-class": "translate-x-full",
            "leave-active-class": "transition-transform duration-200 ease-in motion-reduce:transition-none",
            "leave-to-class": "translate-x-full"
          }, {
            default: _withCtx(() => [
              (_unref(isOpen))
                ? (_openBlock(), _createElementBlock("aside", {
                    key: 0,
                    ref_key: "panelRef",
                    ref: panelRef,
                    role: "dialog",
                    "aria-modal": "true",
                    "aria-labelledby": TITLE_ID,
                    class: _normalizeClass(["setup-drawer fixed inset-0 flex flex-col bg-catering-cream-100 font-catering-body text-catering-ink-900 shadow-catering-xl sm:inset-y-0 sm:left-auto sm:right-0 sm:border-l sm:border-catering-cream-400", importOpen.value ? 'z-20' : 'z-50'])
                  }, [
                    _createElementVNode("header", _hoisted_1, [
                      _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                          _createElementVNode("h2", {
                            id: TITLE_ID,
                            class: "m-0 font-catering-display text-3xl leading-none text-catering-ink-900"
                          }, [
                            _createTextVNode(_toDisplayString(drawerCopy.value.title) + " ", 1),
                            _createElementVNode("em", null, _toDisplayString(drawerCopy.value.emphasis), 1)
                          ]),
                          _createElementVNode("p", _hoisted_4, _toDisplayString(drawerCopy.value.header), 1)
                        ]),
                        _createElementVNode("button", {
                          type: "button",
                          class: "-mr-1.5 cursor-pointer rounded-catering-xs border-none bg-transparent p-1.5 text-catering-ink-500 transition-colors hover:bg-catering-cream-300 hover:text-catering-ink-900",
                          "aria-label": "Close setup",
                          onClick: _cache[1] || (_cache[1] = (...args) => (_unref(close) && _unref(close)(...args)))
                        }, [
                          _createVNode(_unref(XMarkIcon), {
                            class: "h-5 w-5",
                            "aria-hidden": "true"
                          })
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("span", _hoisted_6, [
                          _createElementVNode("span", {
                            class: "block h-full rounded-full bg-catering-ink-900 transition-[width] duration-500 ease-out motion-reduce:transition-none",
                            style: _normalizeStyle({ width: `${_unref(progress).percent}%` })
                          }, null, 4)
                        ]),
                        _createElementVNode("b", _hoisted_7, _toDisplayString(_unref(progress).label), 1)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("p", _hoisted_9, _toDisplayString(announcement.value), 1),
                      (showPanel.value)
                        ? (_openBlock(), _createBlock(SetupReviewPanel, {
                            key: 0,
                            "under-review": underReview.value,
                            onSteps: _cache[2] || (_cache[2] = $event => (showSteps.value = true))
                          }, null, 8, ["under-review"]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(stepViews.value, (step) => {
                              return (_openBlock(), _createBlock(SetupStep, {
                                key: step.key,
                                "step-key": step.key,
                                index: step.index,
                                title: step.title,
                                status: step.status.label,
                                tone: step.status.tone,
                                complete: step.complete,
                                open: openKey.value === step.key,
                                onToggle: $event => (toggle(step.key))
                              }, {
                                default: _withCtx(() => [
                                  (step.key === _unref(STORE_DETAILS_STEP))
                                    ? (_openBlock(), _createBlock(SetupStoreDetailsStep, {
                                        key: 0,
                                        data: step.data,
                                        onDone: $event => (advance(step.key))
                                      }, null, 8, ["data", "onDone"]))
                                    : (step.key === _unref(PAYMENTS_STEP))
                                      ? (_openBlock(), _createBlock(SetupPaymentsStep, {
                                          key: 1,
                                          data: step.data,
                                          complete: step.complete,
                                          onDone: $event => (advance(step.key))
                                        }, null, 8, ["data", "complete", "onDone"]))
                                      : (step.key === _unref(CUSTOMERS_STEP))
                                        ? (_openBlock(), _createBlock(SetupCustomersStep, {
                                            key: 2,
                                            data: step.data,
                                            onImport: _cache[3] || (_cache[3] = $event => (importOpen.value = true)),
                                            onNavigate: _unref(close),
                                            onDone: $event => (advance(step.key))
                                          }, null, 8, ["data", "onNavigate", "onDone"]))
                                        : (step.key === _unref(ASSISTANT_STEP))
                                          ? (_openBlock(), _createBlock(SetupAssistantStep, {
                                              key: 3,
                                              data: step.data,
                                              platform: _unref(platform),
                                              complete: step.complete,
                                              onDone: $event => (advance(step.key))
                                            }, null, 8, ["data", "platform", "complete", "onDone"]))
                                          : _createCommentVNode("", true)
                                ]),
                                _: 2
                              }, 1032, ["step-key", "index", "title", "status", "tone", "complete", "open", "onToggle"]))
                            }), 128))
                          ]))
                    ]),
                    _createElementVNode("footer", _hoisted_11, [
                      _createElementVNode("span", _hoisted_12, _toDisplayString(drawerCopy.value.footer), 1),
                      _createElementVNode("button", {
                        type: "button",
                        class: "cursor-pointer border-none bg-transparent p-0 text-xs font-semibold text-catering-tomato-600 underline underline-offset-2",
                        onClick: _cache[4] || (_cache[4] = (...args) => (_unref(close) && _unref(close)(...args)))
                      }, " Close ")
                    ]),
                    _createVNode(ImportWizardModal, {
                      "is-open": importOpen.value,
                      "import-type": "UsersImport",
                      onClose: _cache[5] || (_cache[5] = $event => (importOpen.value = false)),
                      onCompleted: onImported
                    }, null, 8, ["is-open"])
                  ], 2))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

}